import type {
  SupportSessionEndedSignalRPayload,
  SupportSessionRegisteredSignalRPayload,
  SupportSessionUpdatedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'
import { useSignalR } from '../components/providers/SignalRProvider.vue'
import { createSubscriptionAdder } from './subscription-utils'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'

export type SupportEventHandlers = {
  onSupportSessionRegistered?: (
    payload: SignalRReceiveEvent<SupportSessionRegisteredSignalRPayload>
  ) => void
  onSupportSessionEnded?: (
    payload: SignalRReceiveEvent<SupportSessionEndedSignalRPayload>
  ) => void
  onSupportSessionUpdated?: (
    payload: SignalRReceiveEvent<SupportSessionUpdatedSignalRPayload>
  ) => void
}

export function useSupportSignalREvents(handlers?: SupportEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToSupportSessionRegistered(
    cb: (
      payload: SignalRReceiveEvent<SupportSessionRegisteredSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<SupportSessionRegisteredSignalRPayload>(
      signalREvent.Support.V1.SupportSessionRegistered,
      cb,
      options
    )
  }

  function subscribeToSupportSessionEnded(
    cb: (
      payload: SignalRReceiveEvent<SupportSessionEndedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<SupportSessionEndedSignalRPayload>(
      signalREvent.Support.V1.SupportSessionEnded,
      cb,
      options
    )
  }

  function subscribeToSupportSessionUpdated(
    cb: (
      payload: SignalRReceiveEvent<SupportSessionUpdatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<SupportSessionUpdatedSignalRPayload>(
      signalREvent.Support.V1.SupportSessionUpdated,
      cb,
      options
    )
  }

  function subscribeToSupportSessionEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []
      const addSubscription = createSubscriptionAdder(unsubscribeFunctions)

      addSubscription(
        handlers.onSupportSessionRegistered,
        subscribeToSupportSessionRegistered
      )
      addSubscription(
        handlers.onSupportSessionEnded,
        subscribeToSupportSessionEnded
      )
      addSubscription(
        handlers.onSupportSessionUpdated,
        subscribeToSupportSessionUpdated
      )

      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromSupportSessionEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToSupportSessionEvents,
    unsubscribeFromSupportSessionEvents
  }
}
